import {
	ChangeEvent,
	FunctionComponent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";

import {
	faArrowUpFromBracket,
	faRotateRight,
	faWandMagicSparkles,
} from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon } from "@/components/Icon/Icon";
import { Spinner } from "@/components/Spinner/Spinner";
import { TextArea } from "@/components/TextArea/TextArea";
import {
	useAssignLibraryAssetMutation,
	useUploadToLibraryMutation,
} from "@/pages/Private/redux/media/media.api";
import { FileTypes } from "@/pages/Private/pages/Courses/enums/FileTypes.enum";
import {
	MediaPickerModal,
	MediaSelection,
} from "@/pages/Private/pages/Courses/components/MediaLibrary/MediaPickerModal";
import { AiModelUseCase, MediaJobType } from "@/pages/Private/redux/media/media.schema";
import { useDownloadSceneImageMutation } from "@/pages/Private/redux/media/videoWizard.api";
import {
	DEFAULT_SCENE_STYLE_ID,
	SCENE_STYLE_IDS,
	SceneArtifactKind,
	SceneArtifactState,
	SceneStyleState,
	VideoWizard,
	WizardSceneStyle,
} from "@/pages/Private/redux/media/videoWizard.schema";
import { classNames } from "@/utils/classNames";

import { SceneCard } from "../components/SceneCard";
import { ModelSelectRow } from "../ModelSelectRow";
import { isStepApproved, stepOf } from "../wizard.utils";

interface Step2StyleProps {
	wizard: VideoWizard;
	/** The scenario the upload is filed under — the library is scoped to it. */
	courseId: number;
	isBusy: boolean;
	onSelectStyle: (styleId: string) => void;
	onSelectModel: (aiModelId: number) => void;
	onCreateStyle: (courseFileId: number) => void;
	onUpdateStyle: (styleRowId: number, promptFragment: string) => void;
	onGenerate: (sceneIds?: number[], feedback?: string) => void;
	onApprove: () => void;
}

/** The row id inside a `custom:<n>` style id — what the edit endpoint is addressed by. */
const styleRowIdOf = (styleId: string): number => Number(styleId.split(":")[1]);

/**
 * Step 2 — the graphical style, and one example image per scene.
 *
 * The style is the common look every scene shares; changing it does not regenerate
 * anything, it marks the existing images as no longer showing that look, which is the
 * server's `STALE` and this pane's *"Stil geändert — neu generieren"*. Each image is one
 * job, so the author iterates scene by scene until each one fits.
 */
export const Step2Style: FunctionComponent<Step2StyleProps> = ({
	wizard,
	courseId,
	isBusy,
	onSelectStyle,
	onSelectModel,
	onCreateStyle,
	onUpdateStyle,
	onGenerate,
	onApprove,
}) => {
	const { t } = useTranslation();
	const ts = useCallback(
		(key: string, options?: Record<string, unknown>) => t(`videoWizard.${key}`, options ?? {}),
		[t]
	);

	const [feedback, setFeedback] = useState("");

	const { scenes } = wizard.storyboard;
	const state = stepOf(wizard, 2);
	const approved = isStepApproved(wizard, 2);
	const styleId = wizard.styleId ?? DEFAULT_SCENE_STYLE_ID;
	const uploadedStyles = wizard.styles ?? [];
	const selectedUpload = uploadedStyles.find((style) => style.id === styleId) ?? null;

	const fileInput = useRef<HTMLInputElement>(null);
	const [uploadToLibrary, { isLoading: isUploading }] = useUploadToLibraryMutation();
	const [assignAsset] = useAssignLibraryAssetMutation();
	const [uploadError, setUploadError] = useState("");
	const [downloadImage] = useDownloadSceneImageMutation();

	// The picture's source: the computer, or the media library's picker.
	const [isSourceMenuOpen, setSourceMenuOpen] = useState(false);
	const [isLibraryOpen, setLibraryOpen] = useState(false);
	const sourceMenuRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!isSourceMenuOpen) {
			return undefined;
		}

		const onPointerDown = (event: MouseEvent) => {
			if (!sourceMenuRef.current?.contains(event.target as Node)) {
				setSourceMenuOpen(false);
			}
		};

		document.addEventListener("mousedown", onPointerDown);

		return () => document.removeEventListener("mousedown", onPointerDown);
	}, [isSourceMenuOpen]);

	/**
	 * A library pick becomes a style source. An upload already is a course-file; a
	 * generated image is materialised into one first, the same way assigning it as
	 * content would.
	 */
	const handleLibraryPick = async (selection: MediaSelection) => {
		setLibraryOpen(false);
		setUploadError("");

		try {
			const courseFileId =
				selection.kind === "uploaded"
					? selection.id
					: (
							await assignAsset({
								kind: "generated",
								assetId: selection.id,
								courseId,
								fileAssignment: "Content",
							}).unwrap()
						).id;

			onCreateStyle(courseFileId);
		} catch {
			setUploadError(ts("steps.style.uploadFailed"));
		}
	};

	// Through the API's download endpoint, which sets a download disposition and the
	// readable filename — a plain link to the asset URL can only open a tab (BCM360-330).
	const handleDownload = async (sceneId: number) => {
		try {
			const { blob, filename } = await downloadImage({ id: wizard.id, sceneId }).unwrap();
			const url = URL.createObjectURL(blob);
			const anchor = document.createElement("a");

			anchor.href = url;
			anchor.download = filename;
			anchor.click();
			URL.revokeObjectURL(url);
		} catch {
			// The card keeps showing the image; there is nothing to roll back.
		}
	};

	// The description is edited as a draft and committed on blur: every save is a round
	// trip that stales this style's images, so one per keystroke is not a thing to do.
	const [fragment, setFragment] = useState(selectedUpload?.promptFragment ?? "");

	useEffect(() => {
		setFragment(selectedUpload?.promptFragment ?? "");
	}, [selectedUpload?.id, selectedUpload?.promptFragment]);

	const handleUpload = useCallback(
		async (event: ChangeEvent<HTMLInputElement>) => {
			const file = event.target.files?.[0];

			// Cleared straight away so choosing the same file twice still fires a change.
			event.target.value = "";

			if (!file) {
				return;
			}

			setUploadError("");

			try {
				const stored = await uploadToLibrary({ file, courseId }).unwrap();

				onCreateStyle(stored.id);
			} catch {
				setUploadError(ts("steps.style.uploadFailed"));
			}
		},
		[courseId, onCreateStyle, ts, uploadToLibrary]
	);

	// A scene is "working" while it has a job in flight — the aggregate's, not ours.
	const jobsByScene = useMemo(() => {
		const map = new Map<number, number>();

		for (const job of wizard.activeJobs) {
			if (job.type === MediaJobType.SCENE_IMAGE && job.storyboardSceneId) {
				map.set(job.storyboardSceneId, job.progress);
			}
		}

		return map;
	}, [wizard.activeJobs]);

	const hasImages = scenes.some((scene) => !!scene.image);
	const anyGenerating = jobsByScene.size > 0;
	// The server refuses approval unless every scene has a current image; saying so on the
	// button beats a request that comes back 422.
	const allReady = scenes.every((scene) => scene.image?.state === SceneArtifactState.READY);
	const anyStale = scenes.some((scene) => scene.image?.state === SceneArtifactState.STALE);

	return (
		<div className="space-y-4">
			<header className="flex flex-wrap items-start justify-between gap-3">
				<div>
					<h2 className="text-lg font-bold text-ink">{ts("steps.style.heading")}</h2>
					<p className="mt-1 max-w-[640px] text-xs text-subtle">{ts("steps.style.description")}</p>
				</div>
				<span
					className={classNames(
						"shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold",
						approved
							? "bg-success-light text-text"
							: hasImages
								? "bg-status-running-light text-warning-ink"
								: "bg-surface text-subtle"
					)}
				>
					{approved
						? ts("steps.style.approvedBadge")
						: hasImages
							? ts("status.awaitingApproval")
							: ts("status.notStarted")}
				</span>
			</header>

			<ModelSelectRow
				disabled={isBusy || anyGenerating}
				step={2}
				useCase={AiModelUseCase.SCENE_IMAGE}
				wizard={wizard}
				onSelectModel={onSelectModel}
			/>

			<section className="space-y-3 rounded-card border border-border-soft bg-white p-4">
				<div className="space-y-1">
					<span className="block text-sm font-bold text-ink">{ts("steps.style.styleLabel")}</span>
					{/* Above the tiles rather than below them: it says what choosing one *does*,
					    which is worth knowing before the choice and not after it. */}
					<p className="text-xs text-subtle">{ts("steps.style.styleHint")}</p>
				</div>

				<div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
					{SCENE_STYLE_IDS.map((id) => (
						<button
							key={id}
							className={classNames(
								"rounded-card border px-3 py-2.5 text-left transition-colors",
								id === styleId
									? "border-accent bg-accent-tint"
									: "border-border bg-white hover:border-accent"
							)}
							disabled={isBusy || anyGenerating}
							type="button"
							onClick={() => onSelectStyle(id)}
						>
							<span className="block text-xs font-bold text-ink">
								{ts(`steps.style.styles.${id}.name`)}
							</span>
							<span className="mt-0.5 block text-xs text-subtle">
								{ts(`steps.style.styles.${id}.hint`)}
							</span>
						</button>
					))}

					{/*
					 * An uploaded style is the same kind of thing as a built-in one — its id is
					 * what `styleId` takes — so its tile sits in the same grid and choosing it is
					 * the same call. What differs is that it can show itself: the picture is the
					 * tile's preview, while what the model is given is the description read out
					 * of it.
					 */}
					{uploadedStyles.map((style) => (
						<UploadedStyleTile
							key={style.id}
							disabled={isBusy || anyGenerating}
							isSelected={style.id === styleId}
							style={style}
							ts={ts}
							onSelect={() => onSelectStyle(style.id)}
						/>
					))}
				</div>

				{/*
				 * A real button, below the grid rather than in it. As a dashed tile among the
				 * styles it read as a seventh look you could pick, when it is the one control
				 * here that does something rather than names something.
				 */}
				{/*
				 * Left-aligned under the tiles, and phrased as "or …", so the row reads on from
				 * the six looks above it: one more way to answer the same question rather than a
				 * separate feature parked in the corner.
				 */}
				<div className="flex flex-wrap items-center gap-x-2 gap-y-1">
					<span className="text-xs text-subtle">{ts("steps.style.uploadStyleHint")}</span>
					{/* Where the picture comes from — the computer, or a file already in the
					    media library. A menu, so the button stays one button. */}
					<div ref={sourceMenuRef} className="relative w-fit">
						<Button
							// `Button` is `w-full` by default, which made an action sized for a form
							// row out of one that only has to fit its own label.
							className="!w-auto"
							color={ButtonColor.ACTION_SECONDARY}
							disabled={isBusy || anyGenerating || isUploading}
							image={isUploading ? <Spinner /> : <Icon icon={faArrowUpFromBracket} />}
							size={ButtonSize.S}
							title={ts("steps.style.uploadStyle")}
							type="button"
							onClick={() => setSourceMenuOpen((open) => !open)}
						/>
						{isSourceMenuOpen && (
							<div className="absolute left-0 top-full z-20 mt-1 w-64 rounded-card border border-border bg-white p-1 shadow-custom-xs">
								<button
									className="block w-full rounded px-3 py-2 text-left text-sm font-semibold text-ink hover:bg-surface"
									type="button"
									onClick={() => {
										setSourceMenuOpen(false);
										fileInput.current?.click();
									}}
								>
									{ts("steps.style.sourceComputer")}
								</button>
								<button
									className="block w-full rounded px-3 py-2 text-left text-sm font-semibold text-ink hover:bg-surface"
									type="button"
									onClick={() => {
										setSourceMenuOpen(false);
										setLibraryOpen(true);
									}}
								>
									{ts("steps.style.sourceLibrary")}
								</button>
							</div>
						)}
					</div>
				</div>

				<input
					ref={fileInput}
					accept="image/png,image/jpeg,image/webp"
					className="hidden"
					type="file"
					onChange={handleUpload}
				/>

				{!!uploadError && (
					<p className="rounded-card bg-error-light px-3 py-2 text-xs font-semibold text-text">
						{uploadError}
					</p>
				)}

				{/*
				 * The description, not the picture, is what every scene prompt is given — so it
				 * is shown, and it is editable. An analysis that latched onto the wrong thing is
				 * fixable in place rather than by re-uploading and hoping, and saving stales
				 * exactly this style's images: the key still names them, the look no longer does.
				 */}
				{!!selectedUpload && selectedUpload.state === SceneStyleState.READY && (
					<div className="space-y-1.5 rounded-card border border-border-soft bg-surface p-3">
						<span className="block text-xs font-bold text-ink">
							{ts("steps.style.fragmentLabel")}
						</span>
						<TextArea
							showError
							disabled={isBusy || anyGenerating}
							handleBlur={() => {
								const next = fragment.trim();

								// Any change is submitted — an emptied box included. The server
								// is what refuses an empty description, and its message lands in
								// the pane's error banner instead of the save silently not
								// happening.
								if (next !== (selectedUpload.promptFragment ?? "")) {
									onUpdateStyle(styleRowIdOf(selectedUpload.id), next);
								}
							}}
							handleChange={(event) => setFragment(event.target.value)}
							maxLength={1200}
							name="wizard-style-fragment"
							placeholder={ts("steps.style.fragmentPlaceholder")}
							value={fragment}
						/>
						<p className="text-xs text-subtle">{ts("steps.style.fragmentHint")}</p>
					</div>
				)}
			</section>

			{anyStale && (
				<p className="rounded-card bg-status-running-light px-4 py-2.5 text-sm font-semibold text-warning-ink">
					{ts("steps.style.staleNotice")}
				</p>
			)}

			{!hasImages && !anyGenerating ? (
				<div className="rounded-card border border-dashed border-border bg-surface px-4 py-8 text-center">
					<p className="mx-auto max-w-[520px] text-sm text-subtle">{ts("steps.style.empty")}</p>
					<div className="mt-4 flex justify-center">
						<Button
							color={ButtonColor.ACTION}
							disabled={isBusy || !scenes.length}
							image={<Icon className="mr-2" icon={faWandMagicSparkles} />}
							size={ButtonSize.M}
							title={ts("steps.style.generateAll", {
								scenes: scenes.length,
							})}
							type="button"
							onClick={() => onGenerate()}
						/>
					</div>
				</div>
			) : (
				<>
					<p className="text-sm font-bold text-ink">{ts("steps.style.resultHeading")}</p>

					<div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
						{scenes.map((scene, index) => (
							<SceneCard
								key={scene.id}
								artifact={scene.image}
								disabled={isBusy}
								downloadLabel={ts("steps.style.downloadImage")}
								index={index}
								isGenerating={jobsByScene.has(scene.id)}
								kind={SceneArtifactKind.IMAGE}
								progress={jobsByScene.get(scene.id)}
								regenerateLabel={ts("steps.style.regenerateScene")}
								scene={scene}
								onDownload={() => handleDownload(scene.id)}
								onRegenerate={(sceneFeedback) => onGenerate([scene.id], sceneFeedback)}
							/>
						))}
					</div>

					<section className="space-y-3 rounded-card border border-border-soft bg-surface p-4">
						<div className="flex flex-wrap items-center justify-between gap-2">
							<span className="text-sm font-bold text-ink">{ts("steps.style.iterateHeading")}</span>
							<span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-subtle">
								{ts("steps.style.iterations", {
									count: state?.generationCount ?? 0,
								})}
							</span>
						</div>

						<TextArea
							showError
							handleChange={(event) => setFeedback(event.target.value)}
							name="wizard-image-feedback"
							placeholder={ts("steps.style.feedbackPlaceholder")}
							value={feedback}
						/>

						<div className="flex flex-wrap gap-2">
							<Button
								className="flex-1"
								color={ButtonColor.ACTION_SECONDARY}
								disabled={isBusy || anyGenerating || !scenes.length}
								image={<Icon className="mr-2" icon={faRotateRight} />}
								size={ButtonSize.M}
								title={ts("steps.style.regenerateAll")}
								type="button"
								onClick={() => {
									onGenerate(undefined, feedback.trim() || undefined);
									// Unsent feedback is deliberately not kept: it has been used.
									setFeedback("");
								}}
							/>
							<Button
								color={ButtonColor.ACTION}
								disabled={isBusy || !allReady}
								size={ButtonSize.M}
								title={ts("steps.style.approve")}
								type="button"
								onClick={onApprove}
							/>
						</div>

						{!allReady && (
							<p className="text-xs text-subtle">{ts("steps.style.approveBlockedHint")}</p>
						)}
					</section>
				</>
			)}

			{/* Picking a style picture out of the media library — images only. */}
			{isLibraryOpen && (
				<MediaPickerModal
					styleReferencesOnly
					contentType={FileTypes.Image}
					courseId={courseId}
					handleClose={() => setLibraryOpen(false)}
					isOpened={isLibraryOpen}
					onSelect={handleLibraryPick}
				/>
			)}
		</div>
	);
};

interface UploadedStyleTileProps {
	style: WizardSceneStyle;
	isSelected: boolean;
	disabled: boolean;
	ts: (key: string, options?: Record<string, unknown>) => string;
	onSelect: () => void;
}

/**
 * One uploaded style, in the same grid as the six built-in looks.
 *
 * Only a READY style can be chosen: until the analysis lands there is no description to
 * append to a scene prompt, and a tile that looked chosen while the session generated in a
 * stock look would be the worst failure this feature has. A failed one says why, in the
 * provider's own words, rather than sitting there looking merely unselected.
 */
const UploadedStyleTile: FunctionComponent<UploadedStyleTileProps> = ({
	style,
	isSelected,
	disabled,
	ts,
	onSelect,
}) => {
	const isAnalysing = style.state === SceneStyleState.ANALYSING;
	const hasFailed = style.state === SceneStyleState.FAILED;

	return (
		<button
			className={classNames(
				"overflow-hidden rounded-card border text-left transition-colors",
				isSelected
					? "border-accent bg-accent-tint"
					: hasFailed
						? "border-border bg-error-light"
						: "border-border bg-white hover:border-accent"
			)}
			disabled={disabled || isAnalysing || hasFailed}
			title={style.lastError ?? undefined}
			type="button"
			onClick={onSelect}
		>
			<span className="relative flex aspect-video w-full items-center justify-center bg-surface">
				{!!style.imageUrl && (
					<img alt="" className="h-full w-full object-cover" src={style.imageUrl} />
				)}
				{isAnalysing && (
					<span className="absolute inset-0 flex items-center justify-center">
						<Spinner />
					</span>
				)}
			</span>

			<span className="block px-3 py-2">
				<span className="block text-xs font-bold text-ink">
					{ts("steps.style.uploadedStyle")}
					{style.edited ? ` · ${ts("steps.style.fragmentEdited")}` : ""}
				</span>
				<span className="mt-0.5 block text-xs text-subtle">
					{isAnalysing
						? ts("steps.style.uploadedAnalysing")
						: hasFailed
							? style.lastError || ts("steps.style.uploadedFailed")
							: ts("steps.style.uploadedReady")}
				</span>
			</span>
		</button>
	);
};
